<template>
  <b-sidebar
    id="add-new-content-item-sidebar"
    :visible="isAddNewContentItemSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-add-new-content-item-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          Add New
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />

      </div>

      <!-- BODY -->
      <validation-observer
        #default="{ handleSubmit }"
        ref="refFormObserver"
      >
        <!-- Form -->
        <b-form
          class="p-2"
          autocomplete="off"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >

          <!-- Title -->
          <validation-provider
            #default="validationContext"
            name="Title"
            rules="required|min:3|max:100"
          >
            <b-form-group
              label="Title"
              label-for="title"
            >
              <b-form-input
                id="title"
                v-model="contentItemData.title"
                autofocus
                :state="getValidationState(validationContext)"
                trim
                placeholder=""
                autocomplete="off"
              />

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Byline -->
          <validation-provider
            #default="validationContext"
            name="Byline"
          >
            <b-form-group
              label="Byline"
              label-for="byline"
            >
              <b-form-input
                id="byline"
                v-model="contentItemData.byline"
                :state="getValidationState(validationContext)"
                trim
                placeholder=""
                autocomplete="off"
              />

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Excerpt -->
          <validation-provider
            #default="validationContext"
            name="Excerpt"
          >
            <b-form-group
              label="Excerpt"
              label-for="excerpt"
            >
              <b-form-input
                id="excerpt"
                v-model="contentItemData.excerpt"
                :state="getValidationState(validationContext)"
                trim
                placeholder=""
                autocomplete="off"
              />

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- EVENT: Start Date -->
          <validation-provider
            v-if="contentTypeUrl === 'events'"
            #default="validationContext"
            name="Start Date"
          >
            <b-form-group
              label="Start Date"
              label-for="start"
            >
              <b-form-datepicker
                id="start"
                v-if="contentItemData.content"
                v-model="contentItemData.content.start"
                :state="getValidationState(validationContext)"
                trim
                autocomplete="off"
              />

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>


          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
            >
              Add
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
            >
              Cancel
            </b-button>
          </div>

        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormDatepicker, BFormInvalidFeedback, BButton,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref } from '@vue/composition-api'
import { required } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import router from '@/router'
import { $themeConfig } from '@themeConfig'


// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormDatepicker,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddNewContentItemSidebarActive',
    event: 'update:is-add-new-content-item-sidebar-active',
  },
  props: {
    isAddNewContentItemSidebarActive: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
    }
  },
  computed: {
    $themeConfig() {
      return $themeConfig
    },
  },
  setup(props, { emit }) {
    // Use toast
    const toast = useToast()

    const blankContentItemData = {
      title: '',
      byline: '',
      excerpt: '',
      body: '',
    }

    const contentItemData = ref(JSON.parse(JSON.stringify(blankContentItemData)))
    const contentTypeUrl = ref(router.currentRoute.name)
    const resetcontentItemData = () => {
      contentItemData.value = JSON.parse(JSON.stringify(blankContentItemData))
    }

    const onSubmit = () => {
      store.dispatch('app/addContentItem', { contentType: contentTypeUrl.value, contentData: contentItemData.value })
        .then(response => {
          emit('refetch-data')
          emit('update:is-add-new-content-item-sidebar-active', false)

          router.push({ name: `${contentTypeUrl.value}-view`, params: { id: response.data.results[0].id } })
        })
        .catch(error => {
          if (typeof error.response === 'undefined') {
            // log error
            console.error(error)
          } else {
            // alert user to error
            toast({
              component: ToastificationContent,
              props: {
                title: `Error creating new ${contentTypeUrl.value}.`,
                text: `ERROR: ${error.response.data.message}`,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
            console.log(error.response.data.message)
          }
        })
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetcontentItemData)

    return {
      contentItemData,
      contentTypeUrl,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-new-content-item-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
